import type { AdminContactImportRow } from "../../api/types";

/**
 * Client-side CSV parsing + column mapping for `POST admin/contacts/import`
 * (task 5.1 — the backend only accepts a JSON array, never raw CSV; the SPA
 * parses the file and lets the operator map arbitrary CSV columns onto the
 * fixed set of target fields below). Deliberately minimal: no external CSV
 * library (none is a project dependency yet) — handles the common case
 * (comma-separated, optional `"quoted"` cells, CRLF/LF) and leaves every
 * field-level validation to the server (`AdminContactImportValidator`),
 * which is the actual source of truth for what makes a row valid.
 */

/** Target fields a CSV column can be mapped to — mirrors `AdminContactImportRow`. */
export const TARGET_FIELDS = [
  "email",
  "nombre",
  "empresa",
  "cargo",
  "nivelJerarquico",
  "industria",
  "zonaHoraria",
  "estado",
] as const;

export type TargetField = (typeof TARGET_FIELDS)[number];

/**
 * Which target fields are required for a row to be worth submitting at all —
 * mirrors AdminContactImportValidator.cs' actual reject conditions.
 * `nivelJerarquico` is deliberately NOT here: since commit 7e5df39
 * (2026-09-16, "classify nivel_jerarquico from cargo instead of rejecting
 * the row"), the server never rejects a row over this field — it derives
 * the level from `cargo` via HierarchyLevelClassifier when the mapped
 * column doesn't contain one of the 5 canonical literals. That backend fix
 * predates this constant's last update, so this list had drifted out of
 * sync, forcing operators to manually map a column the server no longer
 * needs (pre-merge validation for PR #263 / issue #258).
 */
export const REQUIRED_TARGET_FIELDS: readonly TargetField[] = ["email", "nombre", "empresa", "cargo"];

/**
 * Display label for each target field — also the exact header text of the
 * app's own downloadable CSV template. Single source of truth for both the
 * mapping table's labels and `autoMapColumns`' auto-detection, so the two
 * can never drift out of sync with each other.
 */
export const FIELD_LABELS: Record<TargetField, string> = {
  email: "Email",
  nombre: "Nombre",
  empresa: "Empresa",
  cargo: "Cargo",
  nivelJerarquico: "Nivel jerarquico",
  industria: "Industria",
  zonaHoraria: "Zona horaria",
  estado: "Estado",
};

/** A CSV column header mapped to each target field, or `""` when that field is not mapped to any column. */
export type ColumnMapping = Record<TargetField, string>;

function normalizeHeader(value: string): string {
  return value.trim().toLowerCase();
}

/**
 * Auto-detects a column mapping by matching each target field's label
 * (`FIELD_LABELS`) OR its raw key name (`TARGET_FIELDS`, e.g.
 * `nivelJerarquico`) against the uploaded headers, case- and
 * whitespace-insensitively. Fixes the reported bug where re-uploading the
 * app's own downloadable template still required mapping every column by
 * hand. Codex review (PR #263): a file exported programmatically by another
 * integration sharing this same JSON schema may use the raw key as its
 * header instead of the display label -- both are checked. Only EXACT
 * matches auto-map -- no fuzzy/partial matching, since a wrong auto-guess
 * would silently mis-populate a field with the wrong column, which is worse
 * than leaving it for a manual pick. A field left unmapped (no header
 * matches either alias) stays `""`, same as before.
 */
export function autoMapColumns(headers: string[]): ColumnMapping {
  const byNormalizedHeader = new Map(headers.map((header) => [normalizeHeader(header), header]));

  return Object.fromEntries(
    TARGET_FIELDS.map((field) => {
      const match = byNormalizedHeader.get(normalizeHeader(FIELD_LABELS[field])) ?? byNormalizedHeader.get(normalizeHeader(field));
      return [field, match ?? ""];
    }),
  ) as ColumnMapping;
}

/**
 * Whether two header lists represent "the same file again" (e.g.
 * re-exported after fixing a typo): the same set of headers, ignoring order
 * and case/whitespace differences. Codex review (PR #263, via RDD finding
 * R3-004): a byte-identical/positional check treated a merely reordered or
 * recased re-export of the same source as "a different file", discarding
 * the operator's manual mapping work. Order-insensitivity is safe because
 * `buildImportRows` looks up columns by header TEXT, not position -- but a
 * caller that treats headers as "matching" despite a casing difference MUST
 * translate any preserved mapping through `remapToNewHeaders` afterwards,
 * or the preserved mapping's stored (old-cased) header text will silently
 * fail that same lookup.
 */
export function headersMatch(a: string[], b: string[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  const normalizedA = new Set(a.map(normalizeHeader));
  const normalizedB = new Set(b.map(normalizeHeader));
  if (normalizedA.size !== normalizedB.size) {
    return false;
  }
  for (const value of normalizedA) {
    if (!normalizedB.has(value)) {
      return false;
    }
  }
  return true;
}

/**
 * Translates an existing mapping's header values to their exact spelling in
 * `newHeaders`, matched case/whitespace-insensitively. Required whenever a
 * mapping is preserved across a `headersMatch`-equal re-upload that reordered
 * or recased its headers: `buildImportRows` looks up a mapped header by
 * exact string in the CURRENT file's headers, so keeping the OLD spelling
 * would silently fail that lookup and drop the field from every row. A
 * mapped field whose header can't be found in `newHeaders` (shouldn't happen
 * when `headersMatch` was true, but kept defensive) is unmapped rather than
 * left pointing at a column that may not exist.
 */
export function remapToNewHeaders(mapping: ColumnMapping, newHeaders: string[]): ColumnMapping {
  const byNormalizedHeader = new Map(newHeaders.map((header) => [normalizeHeader(header), header]));

  return Object.fromEntries(
    TARGET_FIELDS.map((field) => {
      const oldValue = mapping[field];
      if (oldValue === "") {
        return [field, ""];
      }
      return [field, byNormalizedHeader.get(normalizeHeader(oldValue)) ?? ""];
    }),
  ) as ColumnMapping;
}

export interface ParsedCsv {
  headers: string[];
  rows: string[][];
  /**
   * Codex round 6 (PR #386): 1-based row/line number in the SOURCE file of
   * each entry of `rows` (same order). Blank lines/rows and the header are
   * dropped from `rows`, so a backend `fila` index (0-based into the
   * submitted array) does not equal the source row an operator must fix.
   */
  sourceRows?: number[];
}

/**
 * Decodes a raw CSV file's bytes into text, tolerating the two encodings a
 * client is realistically going to hand us: real UTF-8, and Windows-1252 —
 * Excel's default "CSV (Comma delimited)" export on a Spanish-locale Windows
 * (as opposed to the separate "CSV UTF-8" option most operators never pick).
 * Bug found in dev (23 sep): reading every CSV via `FileReader.readAsText`
 * (implicit UTF-8) turned a cp1252 file's accented bytes into U+FFFD
 * replacement characters (e.g. "Control Log�stico") — a single cp1252
 * accented byte (like 0xED for i-acute) is an invalid UTF-8 lead byte on its
 * own, so a strict UTF-8 decode of a genuinely cp1252 file always fails,
 * which is exactly the signal used here to fall back.
 */
export function decodeCsvText(buffer: ArrayBuffer): string {
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(buffer);
  } catch {
    return new TextDecoder("windows-1252").decode(buffer);
  }
}

/**
 * Splits one CSV line into cells, respecting RFC 4180 quoting: a comma inside
 * a `"quoted"` cell is NOT a column separator, and `""` inside a quoted cell
 * is an escaped literal quote. A prior version split on every comma before
 * unquoting, which silently misaligned any row containing a quoted comma
 * (e.g. `"Empresa, S.A."`) — this walks the line character by character
 * instead of using a naive split.
 *
 * Issue #258, hallazgo #7: a closing quote must be followed only by
 * whitespace before the next comma (or the end of the line) -- anything
 * else non-whitespace (e.g. `"Acme"Corp`) is malformed quoting. A prior
 * version silently concatenated that trailing text into the same cell
 * (`AcmeCorp`) instead of surfacing an error, hiding a real formatting
 * problem in the operator's file. Codex review (PR #263, P2): an earlier
 * version of this guard also rejected whitespace PADDING after the closing
 * quote (e.g. `"Acme" ,`) -- a legitimate, previously-working pattern this
 * parser trims everywhere else -- so whitespace is now explicitly allowed
 * to accumulate (and gets trimmed away at cell-push time like any other
 * cell) while still rejecting genuine glued-on text.
 */
function splitLine(line: string): string[] {
  const cells: string[] = [];
  let current = "";
  let inQuotes = false;
  let justClosedQuote = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (inQuotes) {
      if (char === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
          justClosedQuote = true;
        }
      } else {
        current += char;
      }
      continue;
    }

    if (justClosedQuote && char !== ",") {
      if (char !== undefined && /\s/.test(char)) {
        current += char;
        continue;
      }
      throw new Error('CSV mal formado: hay texto pegado justo despues de una comilla de cierre (ej. "Acme"Corp).');
    }

    if (char === '"' && current.trim() === "") {
      inQuotes = true;
      current = "";
    } else if (char === ",") {
      cells.push(current.trim());
      current = "";
      justClosedQuote = false;
    } else {
      current += char;
    }
  }

  cells.push(current.trim());
  return cells;
}

/** Parses raw CSV text into a header row + data rows, both as trimmed string cells. */
export function parseCsv(text: string): ParsedCsv {
  const lines = text
    .split(/\r\n|\r|\n/)
    .map((line, index) => ({ line: line.trim(), sourceRow: index + 1 }))
    .filter(({ line }) => line.length > 0);

  if (lines.length === 0) {
    return { headers: [], rows: [], sourceRows: [] };
  }

  const headerLine = lines[0];
  const dataLines = lines.slice(1);
  return {
    headers: splitLine(headerLine?.line ?? ""),
    rows: dataLines.map(({ line }) => splitLine(line)),
    sourceRows: dataLines.map(({ sourceRow }) => sourceRow),
  };
}

/**
 * Applies a column mapping to every parsed CSV row, producing the JSON
 * payload rows `importContacts` sends. An unmapped optional field (mapping
 * value `""`) is simply omitted from the payload row — the server applies
 * its own default (task 5.2 relies on this: never sends an empty string for
 * `estado`/`zonaHoraria`, letting the server's blank-defaults-to rule apply).
 */
export function buildImportRows(
  headers: string[],
  rows: string[][],
  mapping: ColumnMapping,
): AdminContactImportRow[] {
  const columnIndex = new Map(headers.map((header, index) => [header, index]));

  return rows.map((row) => {
    const payload: Partial<Record<TargetField, string>> = {};

    for (const field of TARGET_FIELDS) {
      const header = mapping[field];
      if (!header) {
        continue;
      }

      const index = columnIndex.get(header);
      if (index === undefined) {
        continue;
      }

      const value = row[index];
      if (value !== undefined && value !== "") {
        payload[field] = value;
      }
    }

    return payload as AdminContactImportRow;
  });
}
